import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/contexts/AuthContext";
import {
  Drop,
  MapPinSimple,
  CloudSun,
  Clock,
  FloppyDisk,
  Camera,
  Sparkle,
  Image as ImageIcon,
  Spinner,
  CheckCircle,
} from "@phosphor-icons/react";

const nowLocalIso = () => {
  const d = new Date();
  d.setSeconds(0, 0);
  const off = d.getTimezoneOffset();
  d.setMinutes(d.getMinutes() - off);
  return d.toISOString().slice(0, 16);
};

export default function NewApplicationPage() {
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [form, setForm] = useState({
    field_id: "",
    product_name: "",
    active_ingredient: "",
    dosage: "",
    dosage_unit: "l/ha",
    treated_area_ha: "",
    target_pest: "",
    waiting_period_days: "",
    weather_temp_c: "",
    weather_conditions: "",
    wind_speed_kmh: "",
    verwendungsart: "freiland",
    applied_at: nowLocalIso(),
    notes: "",
    lat: null,
    lng: null,
  });
  const [saving, setSaving] = useState(false);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/app/fields`).then((r) => setFields(r.data));
    // automatic GPS on open
    captureLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (k, v) => setForm((f) => ({...f, [k]: v }));

  const captureLocation = () => {
    if (!navigator.geolocation) return;
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        update("lat", pos.coords.latitude);
        update("lng", pos.coords.longitude);
        setGpsBusy(false);
      },
      () => setGpsBusy(false),
      { timeout: 8000 }
    );
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const onPhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      toast.error("Bitte ein JPEG, PNG oder WEBP Foto wählen.");
      e.target.value = "";
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Foto zu groß (max. 12 MB).");
      e.target.value = "";
      return;
    }
    setOcrBusy(true);
    setOcrResult(null);
    try {
      const dataUrl = await fileToBase64(file);
      const { data } = await axios.post(
        `${API}/ocr/analyze`,
        { image_base64: dataUrl },
        { timeout: 90000 }
      );

      // Felder ins Formular uebernehmen (nur wenn vorhanden)
      const next = {...form };
      const map = {
        psm_name: "product_name",
        active_ingredient: "active_ingredient",
        dosage: "dosage",
        dosage_unit: "dosage_unit",
        treated_area_ha: "treated_area_ha",
        target_pest: "target_pest",
        waiting_period_days: "waiting_period_days",
        weather_temp_c: "weather_temp_c",
        weather_conditions: "weather_conditions",
        wind_speed_kmh: "wind_speed_kmh",
        verwendungsart: "verwendungsart",
        notes: "notes",
      };
      for (const [k, v] of Object.entries(map)) {
        if (data[k] !== null && data[k] !== undefined && data[k] !== "") {
          next[v] = String(data[k]);
        }
      }
      if (data.applied_at) {
        // GPT liefert "YYYY-MM-DDTHH:MM". passt direkt fuer datetime-local
        next.applied_at = data.applied_at;
      }
      // Falls Notiz schon erkannt + zusaetzliche Hinweise: Datum/Kultur/firma anhaengen
      const extras = [];
      if (data.kultur) extras.push(`Kultur (Foto): ${data.kultur}`);
      if (data.firma) extras.push(`Anwender (Foto): ${data.firma}`);
      if (extras.length) {
        next.notes = [next.notes, extras.join(" · ")].filter(Boolean).join("\n");
      }
      setForm(next);
      setOcrResult(data);

      const conf = Math.round((data.confidence || 0) * 100);
      toast.success(
        data.psm_match
          ? `Erkannt: ${data.psm_match.mittelname} (${conf}% Sicherheit)`
          : `Daten übernommen (${conf}% Sicherheit). Bitte prüfen.`,
        { duration: 5000 }
      );
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Foto-Erkennung fehlgeschlagen.");
    } finally {
      setOcrBusy(false);
      if (e.target) e.target.value = "";
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.field_id) {
      toast.error("Bitte eine Fläche wählen");
      return;
    }
    setSaving(true);
    try {
      const payload = {...form,
        dosage: parseFloat(form.dosage),
        treated_area_ha: form.treated_area_ha ? parseFloat(form.treated_area_ha) : null,
        weather_temp_c: form.weather_temp_c ? parseFloat(form.weather_temp_c) : null,
        wind_speed_kmh: form.wind_speed_kmh ? parseFloat(form.wind_speed_kmh) : null,
        waiting_period_days: form.waiting_period_days
          ? parseInt(form.waiting_period_days, 10)
          : null,
        applied_at: new Date(form.applied_at).toISOString(),
      };
      await axios.post(`${API}/app/applications`, payload);
      toast.success("Anwendung erfasst");
      navigate("/app/applications");
    } catch (err) {
      if (err.response?.status === 402) {
        const d = err.response.data?.detail || {};
        toast.error(d.message || "Free-Limit erreicht. Bitte upgrade auf Pro.", {
          action: {
            label: "Upgrade",
            onClick: () => navigate("/preise"),
          },
          duration: 8000,
        });
      } else {
        const d = err.response?.data?.detail;
        toast.error(typeof d === "string" ? d : "Speichern fehlgeschlagen");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="font-heading text-3xl font-semibold text-forest-900 mb-2">
        Neue Anwendung
      </h1>
      <p className="font-body text-[15px] text-[#4A524C] mb-6">
        Datum, GPS und Wetter werden automatisch erfasst, Sie ergänzen nur
        Mittel und Menge.
      </p>

      {/* OCR-Karte: Foto-Erkennung handschriftlicher Aufzeichnungen */}
      {fields.length > 0 && (
        <div
          data-testid="ocr-card"
          className="relative mb-6 rounded-3xl overflow-hidden bg-gradient-to-br from-forest-700 via-forest-800 to-forest-900 text-white p-5 sm:p-6 shadow-lg ring-1 ring-forest-900/20"
        >
          <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-bone/10 blur-2xl pointer-events-none" aria-hidden="true" />
          <div className="absolute -bottom-8 left-1/3 w-40 h-40 rounded-full bg-terracotta/15 blur-3xl pointer-events-none" aria-hidden="true" />
          <div className="relative flex items-start gap-4">
            <div className="hidden sm:flex w-12 h-12 shrink-0 rounded-2xl bg-white/12 backdrop-blur items-center justify-center ring-1 ring-white/15">
              <Camera size={22} weight="duotone" />
            </div>
            <div className="flex-1">
              <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] bg-white/15 px-2.5 py-1 rounded-full">
                <Sparkle size={12} weight="fill" /> Neu · KI-Erkennung
              </div>
              <h2 className="font-heading text-xl sm:text-2xl font-semibold mt-2">
                Nachträgliche Erfassung per Foto
              </h2>
              <p className="text-sm text-white/80 mt-1 max-w-xl">
                Spritztagebuch, Etikett oder Lieferschein fotografieren. Plantus liest die Daten und füllt das Formular automatisch aus. Du prüfst nur noch nach.
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={ocrBusy}
                  data-testid="ocr-camera-btn"
                  className="inline-flex items-center gap-2 bg-white text-forest-900 hover:bg-bone font-semibold text-sm px-4 py-2.5 rounded-full transition-all shadow-sm hover:shadow-md disabled:opacity-60"
                >
                  {ocrBusy ? (
                    <>
                      <Spinner size={16} className="animate-spin" /> Analysiere…
                    </>
                  ) : (
                    <>
                      <Camera size={16} weight="bold" /> Foto aufnehmen
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={ocrBusy}
                  data-testid="ocr-upload-btn"
                  className="inline-flex items-center gap-2 bg-white/12 hover:bg-white/20 backdrop-blur font-medium text-sm px-4 py-2.5 rounded-full ring-1 ring-white/15 disabled:opacity-60"
                >
                  <ImageIcon size={16} weight="bold" /> Aus Galerie wählen
                </button>
              </div>

              {ocrResult && (
                <div
                  data-testid="ocr-result-badge"
                  className="mt-4 inline-flex items-center gap-2 bg-emerald-500/15 text-emerald-100 text-xs font-medium px-3 py-1.5 rounded-full ring-1 ring-emerald-300/30"
                >
                  <CheckCircle size={14} weight="fill" />
                  Übernommen · {Math.round((ocrResult.confidence || 0) * 100)}% Sicherheit
                  {ocrResult.missing_fields?.length > 0 && (
                    <span className="opacity-80">
                      · noch zu ergänzen: {ocrResult.missing_fields.join(", ")}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            onChange={onPhotoSelected}
            className="hidden"
            data-testid="ocr-camera-input"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={onPhotoSelected}
            className="hidden"
            data-testid="ocr-file-input"
          />
        </div>
      )}

      {fields.length === 0 ? (
        <div className="bg-white rounded-3xl border border-bone-200 p-8 text-center">
          <div className="font-heading text-lg text-forest-900 mb-2">
            Erst eine Fläche anlegen
          </div>
          <div className="font-body text-sm text-[#4A524C]">
            Sie benötigen mindestens eine Fläche, bevor Sie eine Anwendung erfassen
            können.
          </div>
        </div>
      ) : (
        <form
          onSubmit={onSubmit}
          data-testid="new-application-form"
          className="bg-white rounded-3xl border border-bone-200 p-6 sm:p-8 space-y-5"
        >
          <Section title="Was und wo" icon={Drop}>
            <Select
              label="Fläche"
              value={form.field_id}
              onChange={(v) => update("field_id", v)}
              testId="napp-field"
              required
            >
              <option value="">, bitte wählen,</option>
              {fields.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                  {f.crop ? ` · ${f.crop}` : ""}
                </option>
              ))}
            </Select>
            <Select
              label="Art der Verwendung (EU 2023/564)"
              value={form.verwendungsart}
              onChange={(v) => update("verwendungsart", v)}
              testId="napp-verwendungsart"
              required
            >
              <option value="freiland">Freiland</option>
              <option value="gewaechshaus">Gewächshaus</option>
              <option value="saatgutbehandlung">Saatgutbehandlung</option>
            </Select>
            <Grid>
              <Input
                label="Pflanzenschutzmittel"
                value={form.product_name}
                onChange={(v) => update("product_name", v)}
                required
                testId="napp-product"
              />
              <Input
                label="Wirkstoff (optional)"
                value={form.active_ingredient}
                onChange={(v) => update("active_ingredient", v)}
                testId="napp-active"
              />
            </Grid>
            <Grid cols={3}>
              <Input
                label="Menge"
                type="number"
                step="0.01"
                value={form.dosage}
                onChange={(v) => update("dosage", v)}
                required
                testId="napp-dosage"
              />
              <Select
                label="Einheit"
                value={form.dosage_unit}
                onChange={(v) => update("dosage_unit", v)}
                testId="napp-unit"
              >
                <option value="l/ha">l/ha</option>
                <option value="kg/ha">kg/ha</option>
                <option value="g/ha">g/ha</option>
                <option value="ml/ha">ml/ha</option>
              </Select>
              <Input
                label="Behandelte Fläche (ha)"
                type="number"
                step="0.01"
                value={form.treated_area_ha}
                onChange={(v) => update("treated_area_ha", v)}
                testId="napp-area"
              />
            </Grid>
            <Grid>
              <Input
                label="Schaderreger"
                value={form.target_pest}
                onChange={(v) => update("target_pest", v)}
                placeholder="z. B. Mehltau"
                testId="napp-pest"
              />
              <Input
                label="Wartezeit (Tage)"
                type="number"
                value={form.waiting_period_days}
                onChange={(v) => update("waiting_period_days", v)}
                testId="napp-wait"
              />
            </Grid>
          </Section>

          <Section title="Zeit & Wetter" icon={CloudSun}>
            <Grid>
              <Input
                label="Zeitpunkt"
                type="datetime-local"
                value={form.applied_at}
                onChange={(v) => update("applied_at", v)}
                required
                testId="napp-time"
              />
              <Input
                label="Temperatur (°C)"
                type="number"
                step="0.1"
                value={form.weather_temp_c}
                onChange={(v) => update("weather_temp_c", v)}
                testId="napp-temp"
              />
            </Grid>
            <Grid>
              <Input
                label="Wetter"
                value={form.weather_conditions}
                onChange={(v) => update("weather_conditions", v)}
                placeholder="bewölkt, trocken …"
                testId="napp-weather"
              />
              <Input
                label="Wind (km/h)"
                type="number"
                step="0.1"
                value={form.wind_speed_kmh}
                onChange={(v) => update("wind_speed_kmh", v)}
                testId="napp-wind"
              />
            </Grid>
          </Section>

          <Section title="Position" icon={MapPinSimple}>
            <div className="flex items-center gap-3 text-sm">
              <div className="flex-1 font-body text-[#4A524C]">
                {form.lat != null && form.lng != null ? (
                  <>
                    GPS: <strong className="text-forest-700">{form.lat.toFixed(5)}, {form.lng.toFixed(5)}</strong>
                  </>
                ) : (
                  <span className="text-[#7A827C]">Noch keine GPS-Position erfasst</span>
                )}
              </div>
              <button
                type="button"
                onClick={captureLocation}
                disabled={gpsBusy}
                data-testid="napp-gps"
                className="inline-flex items-center gap-2 bg-bone hover:bg-bone-100 border border-bone-200 text-forest-700 px-3.5 py-2 rounded-lg text-sm font-medium transition"
              >
                <MapPinSimple size={14} weight="bold" />
                {gpsBusy ? "Erfasse …" : "GPS aktualisieren"}
              </button>
            </div>
          </Section>

          <Section title="Notiz" icon={Clock}>
            <TextArea
              label="Bemerkungen (optional)"
              value={form.notes}
              onChange={(v) => update("notes", v)}
              testId="napp-notes"
            />
          </Section>

          <div className="pt-2">
            <button
              type="submit"
              disabled={saving}
              data-testid="napp-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-forest-600 hover:bg-forest-700 disabled:opacity-60 text-white font-heading font-medium px-6 py-3.5 rounded-xl transition shadow-sm"
            >
              <FloppyDisk size={18} weight="bold" />
              {saving ? "Speichere …" : "Anwendung speichern"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

const Section = ({ title, icon: Icon, children }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-2 pt-2">
      <Icon size={18} weight="duotone" className="text-forest-700" />
      <h3 className="font-heading text-sm font-semibold uppercase tracking-[0.18em] text-forest-700">
        {title}
      </h3>
    </div>
    {children}
  </div>
);

const Grid = ({ children, cols = 2 }) => {
  const gridClass = cols === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2";
  return <div className={`grid grid-cols-1 ${gridClass} gap-4`}>{children}</div>;
};

const Input = ({ label, testId, onChange,...props }) => (
  <div>
    <label className="block font-body text-sm font-medium text-forest-900 mb-1.5">
      {label}
    </label>
    <input
      data-testid={testId}
      onChange={(e) => onChange?.(e.target.value)}
      className="w-full font-body text-[15px] px-3.5 py-2.5 rounded-xl border border-bone-200 bg-bone focus:outline-none focus:border-forest-600 focus:ring-2 focus:ring-forest-600/15 transition"
      {...props}
    />
  </div>
);

const Select = ({ label, children, testId, onChange,...props }) => (
  <div>
    <label className="block font-body text-sm font-medium text-forest-900 mb-1.5">
      {label}
    </label>
    <select
      data-testid={testId}
      onChange={(e) => onChange?.(e.target.value)}
      className="w-full font-body text-[15px] px-3.5 py-2.5 rounded-xl border border-bone-200 bg-bone focus:outline-none focus:border-forest-600 focus:ring-2 focus:ring-forest-600/15 transition"
      {...props}
    >
      {children}
    </select>
  </div>
);

const TextArea = ({ label, testId, onChange, value,...props }) => (
  <div>
    <label className="block font-body text-sm font-medium text-forest-900 mb-1.5">
      {label}
    </label>
    <textarea
      data-testid={testId}
      onChange={(e) => onChange?.(e.target.value)}
      value={value}
      rows={3}
      className="w-full font-body text-[15px] px-3.5 py-2.5 rounded-xl border border-bone-200 bg-bone focus:outline-none focus:border-forest-600 focus:ring-2 focus:ring-forest-600/15 transition resize-none"
      {...props}
    />
  </div>
);
